import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Rocket, ArrowLeft, Zap, Clock, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import BottomNav from '../components/BottomNav';
import { useAuth } from '../contexts/AuthContext';
import { getBoostPackages, activateBoost, getProfile } from '../services/api';

const formatCountdown = (isoUntil) => {
  if (!isoUntil) return null;
  const end = new Date(isoUntil).getTime();
  const diff = end - Date.now();
  if (diff <= 0) return null;
  const m = Math.floor(diff / 60000);
  const h = Math.floor(m / 60);
  const rem = m % 60;
  if (h > 0) return `${h}h ${rem}m left`;
  return `${m}m left`;
};

const BoostPage = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(null);
  const [error, setError] = useState('');
  const [boostUntil, setBoostUntil] = useState(user?.boost_until || null);
  const [countdown, setCountdown] = useState(formatCountdown(user?.boost_until));

  useEffect(() => {
    getBoostPackages().then(r => setPackages(r.data || [])).catch(() => {}).finally(() => setLoading(false));
    // Refresh user for accurate credits/boost state
    getProfile().then(r => {
      updateUser(r.data);
      setBoostUntil(r.data?.boost_until || null);
      setCountdown(formatCountdown(r.data?.boost_until));
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!boostUntil) return;
    const iv = setInterval(() => setCountdown(formatCountdown(boostUntil)), 30000);
    return () => clearInterval(iv);
  }, [boostUntil]);

  const handleActivate = async (pkg) => {
    setError('');
    setActivating(pkg.boost_id);
    try {
      const res = await activateBoost(pkg.boost_id);
      setBoostUntil(res.data.boost_until);
      setCountdown(formatCountdown(res.data.boost_until));
      const fresh = await getProfile();
      updateUser(fresh.data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not activate boost');
    } finally {
      setActivating(null);
    }
  };

  const totalCredits = (user?.credits || 0) + (user?.promo_credits || 0);

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white pb-24" data-testid="boost-page">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white/95 backdrop-blur-lg border-b border-purple-100">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-full hover:bg-purple-50" data-testid="boost-back-btn">
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Outfit' }}>Boost your profile</h1>
            <p className="text-xs text-gray-500">Rise to the top of Discover</p>
          </div>
          <div className="text-right">
            <div className="text-xs text-gray-500">Credits</div>
            <div className="font-bold text-purple-600" data-testid="boost-credits">{totalCredits}</div>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 pt-4 space-y-4">
        {/* Active boost banner */}
        {boostUntil && countdown && (
          <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-2xl p-4 flex items-center gap-3" data-testid="active-boost-banner">
            <CheckCircle2 className="w-6 h-6" />
            <div className="flex-1">
              <div className="font-semibold">Boost is active</div>
              <div className="text-sm opacity-90 flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {countdown}</div>
            </div>
          </div>
        )}

        {/* Hero */}
        <div className="bg-white rounded-2xl p-5 border border-purple-100 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
              <Rocket className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="font-bold text-gray-900 text-lg" style={{ fontFamily: 'Outfit' }}>Get up to 10× more views</h2>
              <p className="text-sm text-gray-600 mt-1">
                Boost puts your profile at the top of Discover for everyone who fits your preferences. Perfect during evenings and weekends when people are most active.
              </p>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 text-red-600 rounded-xl p-3 text-sm" data-testid="boost-error">{error}</div>
        )}

        {/* Packages */}
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
          </div>
        ) : (
          <div className="space-y-3">
            {packages.map((pkg) => {
              const insufficient = totalCredits < pkg.cost_credits;
              return (
                <div key={pkg.boost_id} className="bg-white rounded-2xl p-4 border border-gray-100 flex items-center gap-3" data-testid={`boost-pkg-${pkg.boost_id}`}>
                  <div className="w-11 h-11 rounded-xl bg-purple-50 flex items-center justify-center">
                    <Zap className="w-5 h-5 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-gray-900">{pkg.name}</div>
                    <div className="text-xs text-gray-500">{pkg.duration_minutes} minutes of premium placement</div>
                  </div>
                  <Button
                    onClick={() => handleActivate(pkg)}
                    disabled={insufficient || activating === pkg.boost_id}
                    className="h-10 rounded-full bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold px-4"
                    data-testid={`boost-activate-${pkg.boost_id}`}
                  >
                    {activating === pkg.boost_id ? <Loader2 className="w-4 h-4 animate-spin" /> : `${pkg.cost_credits} cr`}
                  </Button>
                </div>
              );
            })}
            {packages.length === 0 && (
              <div className="text-center text-gray-500 py-16" data-testid="boost-empty">
                No boost packages available right now.
              </div>
            )}
          </div>
        )}

        {totalCredits < (packages[0]?.cost_credits || 0) && (
          <Button
            onClick={() => navigate('/pricing')}
            className="w-full h-12 rounded-full bg-[#009E60] hover:bg-[#007A4B] text-white font-semibold"
            data-testid="boost-get-credits"
          >
            Get more credits
          </Button>
        )}
      </div>

      <BottomNav />
    </div>
  );
};

export default BoostPage;
